import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DemoCardProps {
  accent: string;
  children: React.ReactNode;
  className?: string;
  testId?: string;
  title?: string;
  footer?: React.ReactNode;
}

export function DemoCard({
  accent,
  children,
  className,
  testId,
  title,
  footer,
}: DemoCardProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "relative flex flex-col gap-5 p-6 rounded-2xl overflow-hidden group",
        "transition-all duration-400 ease-out",
        className
      )}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow: `inset 0 0 0 1px ${accent}22, 0 4px 18px -8px ${accent}50, 0 2px 4px -2px ${accent}30`,
          borderRadius: "inherit",
        }}
      />
      {title && (
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase select-none">
          <span
            className="inline-block w-2 h-2 rounded-full shadow"
            style={{ background: accent, boxShadow: `0 0 0 3px ${accent}22` }}
          />
          {title}
        </div>
      )}
      <div className="relative space-y-4">{children}</div>
      {footer && (
        <div className="relative flex flex-wrap gap-3 pt-1">{footer}</div>
      )}
      <div
        className="absolute inset-0 transition-opacity duration-500 opacity-0 pointer-events-none rounded-2xl group-hover:opacity-100"
        style={{
          background: `linear-gradient(120deg, ${accent}10, transparent 70%)`,
        }}
      />
    </div>
  );
}

interface DemoResultCardProps {
  accent: string;
  title: string;
  loading: boolean;
  loadingLabel?: string;
  result: any | null;
  emptyMessage?: string;
  testId?: string;
}

export function DemoResultCard({
  accent,
  title,
  loading,
  loadingLabel = "Working...",
  result,
  emptyMessage = "Result will appear here.",
  testId = "demo-result",
}: DemoResultCardProps) {
  const [view, setView] = useState<"json" | "list">("json");
  const [copied, setCopied] = useState(false);
  const [highlight, setHighlight] = useState(false);

  // trigger highlight when a fresh result appears
  useEffect(() => {
    if (result) {
      setHighlight(true);
      const t = setTimeout(() => setHighlight(false), 1200);
      return () => clearTimeout(t);
    }
  }, [result]);

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(
        view === "json" ? JSON.stringify(result, null, 2) : formattedPlain()
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  const formattedPlain = () => {
    if (!result || typeof result !== "object") return String(result ?? "");
    const lines: string[] = [];
    Object.entries(result).forEach(([k, v]) => {
      if (typeof v === "object") {
        lines.push(`${k}:`);
        lines.push(
          JSON.stringify(v, null, 2)
            .split("\n")
            .map((l) => "  " + l)
            .join("\n")
        );
      } else {
        lines.push(`${k}: ${v}`);
      }
    });
    return lines.join("\n");
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        "relative flex flex-col gap-0 rounded-2xl overflow-hidden backdrop-blur-sm",
        "transition-all duration-500"
      )}
      style={{
        background: `linear-gradient(175deg, hsl(var(--background)) 0%, ${accent}0d 65%, ${accent}12 100%)`,
      }}
    >
      {/* header */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 text-[11px] font-semibold tracking-wide uppercase relative">
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: accent }}
          />
          {title}
        </div>
        <div className="flex items-center gap-1">
          {result && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setView(view === "json" ? "list" : "json")}
              className="h-6 px-2 text-[10px]"
            >
              {view === "json" ? "Plain" : "Pretty"}
            </Button>
          )}
          {result && (
            <Button
              type="button"
              size="sm"
              onClick={handleCopy}
              disabled={loading}
              className="h-6 px-2 text-[10px]"
              style={{
                boxShadow: `0 1px 3px -1px ${accent}60`,
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          )}
        </div>
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background: `linear-gradient(90deg, ${accent}18, transparent 70%)`,
            mixBlendMode: "overlay",
          }}
        />
      </div>
      {/* content */}
      <div
        className="relative m-4 mb-5 rounded-xl p-4 text-xs font-mono whitespace-pre-wrap min-h-[240px] transition-all duration-500"
        style={{
          background: result ? `${accent}05` : `${accent}03`,
          boxShadow: `inset 0 0 0 1px ${accent}25, 0 4px 10px -6px ${accent}40`,
          border: "1px solid transparent",
        }}
      >
        {result ? (
          <pre
            className={cn(
              "m-0 leading-relaxed transition-opacity duration-500",
              highlight ? "opacity-100" : "opacity-100"
            )}
            data-testid="demo-result-content"
            style={{
              animation: highlight ? "demoFlash 900ms ease-out" : undefined,
            }}
          >
            {view === "json"
              ? JSON.stringify(result, null, 2)
              : formattedPlain()}
          </pre>
        ) : (
          <span className="text-muted-foreground">{emptyMessage}</span>
        )}
        {loading && (
          <div
            className="absolute inset-0 flex items-center justify-center transition-opacity rounded-xl backdrop-blur-sm"
            style={{ background: accent + "18" }}
          >
            <span
              className="flex items-center gap-2 text-[11px] font-medium"
              style={{ color: accent }}
            >
              <span
                className="w-4 h-4 border-2 rounded-full animate-spin"
                style={{ borderColor: accent + "40", borderRightColor: accent }}
              />
              {loadingLabel}
            </span>
          </div>
        )}
        {/* highlight ring when new result appears */}
        {highlight && !loading && (
          <span
            className="absolute inset-0 pointer-events-none rounded-xl"
            style={{
              boxShadow: `0 0 0 1px ${accent}55, 0 0 0 4px ${accent}22`,
              animation: "ringFade 1100ms ease-out forwards",
            }}
          />
        )}
      </div>
      {/* decorative outer soft ring */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow: `inset 0 0 0 1px ${accent}25, 0 8px 28px -12px ${accent}35`,
        }}
      />
      <style>{`
        @keyframes demoFlash {0%{background:${accent}18;}60%{background:transparent;}100%{background:transparent;}}
        @keyframes ringFade {0%{opacity:1}70%{opacity:.4}100%{opacity:0}}
      `}</style>
    </div>
  );
}
